import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import Dropzone from "@/components/Dropzone";
import { ComplianceScoreBadge } from "@/components/ComplianceScoreBadge";
import { FolderSimple, DownloadSimple, Trash, Share, Copy, CheckCircle, Circle, LockKey, ShieldCheck } from "@phosphor-icons/react";

const kb = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function DataRoom() {
  const [data, setData] = useState(null);
  const [folder, setFolder] = useState("Corporate");

  const load = useCallback(async () => {
    const r = await api.get("/dataroom");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (file) => {
    if (file.size > 12 * 1024 * 1024) return toast.error("File too large (max 12 MB).");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/dataroom/upload", { folder, name: file.name, file_mime: file.type || "application/octet-stream", file_base64: reader.result });
        toast.success(`Uploaded to ${folder}`); load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    };
    reader.readAsDataURL(file);
  };

  const download = async (fid, name) => {
    try {
      const res = await api.get(`/dataroom/file/${fid}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const del = async (fid) => { await api.delete(`/dataroom/file/${fid}`); load(); };

  const folderFiles = useMemo(() => (data?.files || []).filter((f) => f.folder === folder), [data, folder]);

  if (!data) return <div className="text-white/40 text-sm">Loading…</div>;

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-2">Data Room</div>
        <h1 className="font-display text-4xl">Investor-ready in one place</h1>
        <p className="text-white/60 text-sm mt-2">Organise your due-diligence documents, track completeness, and share a clean read-only link with investors.</p>
      </div>

      <ShareCard share={data.share} onChange={load} />

      <ComplianceScoreBadge health={data.health} />

      {data.share.enabled && <EmbedBadge share={data.share} />}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="folders">
            {data.folders.map((f) => (
              <button key={f.name} onClick={() => setFolder(f.name)} data-testid={`folder-${f.name}`}
                className={`glass rounded-xl p-4 text-left transition-all ${folder === f.name ? "ring-1 ring-[#FFB020]" : "hover:bg-white/[0.04]"}`}>
                <FolderSimple size={22} weight="duotone" className="text-[#FFB020]" />
                <div className="font-display text-sm mt-2">{f.name}</div>
                <div className="text-white/45 text-xs">{f.count} file{f.count === 1 ? "" : "s"}</div>
              </button>
            ))}
          </div>

          <div className="glass rounded-2xl p-6" data-testid="folder-view">
            <div className="eyebrow mb-4">{folder}</div>
            <Dropzone accept="*" label={`Add to ${folder}`} sublabel="PDF, images, spreadsheets — up to 12 MB" onFile={onFile} testId="dataroom-dropzone" />
            <div className="mt-4 space-y-1">
              {folderFiles.length === 0 ? <div className="text-white/40 text-sm">No files in {folder} yet.</div> : folderFiles.map((f) => (
                <div key={f.file_id} className="flex items-center justify-between py-2 border-b border-white/5" data-testid={`file-${f.file_id}`}>
                  <div className="min-w-0"><div className="text-sm truncate">{f.name}</div><div className="text-white/40 text-xs">{kb(f.size)} · {f.created_at?.slice(0, 10)}</div></div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => download(f.file_id, f.name)} className="text-white/50 hover:text-[#FFB020]" data-testid={`dl-file-${f.file_id}`}><DownloadSimple size={16} /></button>
                    <button onClick={() => del(f.file_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`del-file-${f.file_id}`}><Trash size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Checklist checklist={data.checklist} onChange={load} />
      </div>
    </div>
  );
}

function EmbedBadge({ share }) {
  const dataUrl = `${window.location.origin}/data-room/${share.token}`;
  const badgeUrl = `${API}/public/badge/${share.token}.svg`;
  const html = `<a href="${dataUrl}"><img src="${badgeUrl}" alt="Compliance verified by PerCen360" width="340" height="72" /></a>`;
  const markdown = `[![Compliance verified by PerCen360](${badgeUrl})](${dataUrl})`;
  const copy = (text, what) => { navigator.clipboard.writeText(text); toast.success(`${what} copied`); };
  return (
    <div className="glass rounded-2xl p-6" data-testid="embed-badge">
      <div className="eyebrow mb-1">Embeddable trust badge</div>
      <p className="text-white/55 text-sm mb-4">Drop this live &ldquo;Compliance verified&rdquo; badge into your pitch deck, website or email signature. It links to your investor data room and updates automatically.</p>
      <div className="rounded-xl bg-black/30 border border-white/5 p-5 flex justify-center mb-4">
        <img src={badgeUrl} alt="Compliance verified by PerCen360" width="340" height="72" data-testid="badge-preview" />
      </div>
      <div className="space-y-2">
        <button onClick={() => copy(badgeUrl, "Image URL")} className="btn-ghost text-xs w-full justify-between" data-testid="copy-badge-url"><span className="truncate">Image URL (for decks / signatures)</span><Copy size={14} /></button>
        <button onClick={() => copy(html, "HTML snippet")} className="btn-ghost text-xs w-full justify-between" data-testid="copy-badge-html"><span>HTML snippet (linked)</span><Copy size={14} /></button>
        <button onClick={() => copy(markdown, "Markdown")} className="btn-ghost text-xs w-full justify-between" data-testid="copy-badge-md"><span>Markdown</span><Copy size={14} /></button>
      </div>
    </div>
  );
}

function ShareCard({ share, onChange }) {
  const shareUrl = share.token ? `${window.location.origin}/data-room/${share.token}` : "";
  const enable = async () => { await api.post("/dataroom/share"); toast.success("Investor link enabled"); onChange(); };
  const revoke = async () => { await api.post("/dataroom/share/revoke"); toast.success("Link revoked"); onChange(); };
  const copy = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };
  return (
    <div className="glass rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4" data-testid="share-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#00A86B]/15 flex items-center justify-center shrink-0"><ShieldCheck size={20} weight="duotone" className="text-[#00A86B]" /></div>
        <div>
          <div className="font-display text-lg">Investor share link</div>
          <p className="text-white/55 text-sm">{share.enabled ? "Anyone with the link can view & download — read-only. Revoke anytime." : "Generate a secure read-only link to share your data room with investors."}</p>
          {share.enabled && (
            <div className="flex items-center gap-2 mt-2">
              <code className="text-xs bg-black/30 px-2 py-1 rounded text-[#FFB020] truncate max-w-[280px]" data-testid="share-url">{shareUrl}</code>
              <button onClick={copy} className="text-white/50 hover:text-white" data-testid="copy-share"><Copy size={15} /></button>
            </div>
          )}
        </div>
      </div>
      {share.enabled
        ? <button onClick={revoke} className="btn-ghost text-sm" data-testid="revoke-share"><LockKey size={15} /> Revoke link</button>
        : <button onClick={enable} className="btn-gold text-sm" data-testid="enable-share"><Share size={15} /> Enable link</button>}
    </div>
  );
}

function Checklist({ checklist, onChange }) {
  const toggle = async (item) => {
    try { await api.post("/dataroom/checklist", { item_id: item.item_id, done: !item.done, note: item.note || "" }); onChange(); }
    catch { toast.error("Failed"); }
  };
  const cats = [...new Set(checklist.items.map((i) => i.category))];
  return (
    <div className="glass rounded-2xl p-6" data-testid="dd-checklist">
      <div className="flex items-center justify-between mb-1"><div className="eyebrow">Due-diligence checklist</div><span className="mono text-sm text-[#FFB020]">{checklist.pct}%</span></div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-5"><div className="h-full rounded-full bg-[#00A86B] transition-all" style={{ width: `${checklist.pct}%` }} data-testid="dd-progress" /></div>
      <div className="space-y-4 max-h-[520px] overflow-auto pr-1">
        {cats.map((cat) => (
          <div key={cat}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{cat}</div>
            {checklist.items.filter((i) => i.category === cat).map((item) => (
              <button key={item.item_id} onClick={() => toggle(item)} data-testid={`dd-${item.item_id}`}
                className="flex items-start gap-2 w-full text-left py-1.5 group">
                {item.done ? <CheckCircle size={18} weight="fill" className="text-[#00A86B] shrink-0 mt-0.5" /> : <Circle size={18} className="text-white/25 group-hover:text-white/50 shrink-0 mt-0.5" />}
                <span className={`text-sm ${item.done ? "text-white/50 line-through" : "text-white/80"}`}>{item.label}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
